<template>
  <div class="mb-[10em]">
    <TypeBasedTransition transition-type="fadeInOut">
      <!-- Show both buttons if showJumpButton is true and no errors are shown -->
      <div v-if="showJumpButton && !showErrors">
        <Button
          type="button"
          class="rounded-lg"
          :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
          @click="handleNext"
        >
          Weiter
        </Button>
        <Button
          type="button"
          variant="link"
          class="ml-2"
          @click="handleJumpToResultsPage"
        >
          Zur Übersicht springen
        </Button>
      </div>
      <!-- Show single button if showJumpButton is false and no errors are shown -->
      <Button
        v-else-if="!showJumpButton && !showErrors"
        type="button"
        class="rounded-lg"
        :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
        @click="handleNext"
      >
        Weiter
      </Button>
      <!-- Show errors if validation fails -->
      <div v-if="showErrors">
        <div
          v-for="error in silentErrors"
          :key="error.$uid"
          class="input-errors error-msg"
        >
          <Icon icon="ep:warning-filled" />
          {{ error.$message }}
        </div>
        <Button
          type="button"
          class="mt-4 rounded-lg"
          :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
          @click="handleNext"
        >
          Weiter
        </Button>
      </div>
    </TypeBasedTransition>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, onBeforeMount, ref, watch } from 'vue'
  import { Icon } from '@iconify/vue'
  import TypeBasedTransition from '../transitions/typeBasedTransition'
  import { Button } from '@/components/ui/button'
  import { useKeyboardNav } from '@/composables/useKeyboardNav'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { useThemeColor } from '@/composables/useThemeColor'
  import type { FormState } from '@/types'

  const clientActionsStore = useClientActionStore()
  const showJumpButton = ref(false)
  const showErrors = ref(false)
  const hasAttemptedSubmit = ref(false)

  onBeforeMount(() => {
    const storedObject = localStorage.getItem('shouldShowJumpButton')
    if (storedObject !== null) {
      const shouldShowObject = JSON.parse(storedObject)
      if (
        Object.prototype.hasOwnProperty.call(
          shouldShowObject,
          clientActionsStore.currentSubmissionId
        )
      ) {
        showJumpButton.value = shouldShowObject[clientActionsStore.currentSubmissionId]
      } else {
        showJumpButton.value = false
      }
    }
  })

  const props = defineProps({
    buttonText: {
      type: String,
      default: 'Okay'
    },
    silentErrors: {
      type: Array,
      default: () => []
    },
    showValidateErrors: {
      type: Boolean,
      default: false
    }
  })

  const vueform = inject('vueform', {
    formState: ref<FormState>({
      activeFieldId: '',
      activeFieldIndex: 0,
      valid: false,
      next: false,
      formLength: 0,
      isLastField: false,
      isFirstField: computed(() => true),
      errorLength: 0
    }),
    onSubmit: () => {},
    setToLastField: () => {}
  })

  const formState = computed(() => vueform.formState.value)
  const themeColor = computed(() => useThemeColor().value)

  const emits = defineEmits(['back', 'next', 'jumpToResultsPage'])

  const handleNext = () => {
    if (!hasAttemptedSubmit.value) {
      hasAttemptedSubmit.value = true
    }

    if (formState.value.valid) {
      showErrors.value = false
      emits('next')
    } else {
      showErrors.value = true
    }
  }

  const handleJumpToResultsPage = () => {
    emits('jumpToResultsPage')
  }

  watch(
    () => formState.value.valid,
    (newValid) => {
      if (hasAttemptedSubmit.value && newValid) {
        showErrors.value = false
      }
    }
  )

  useKeyboardNav(emits, formState)
</script>
